<template>
  <div v-if="company" class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur tw-shadow-sm tw-shadow-slate-200/50">
    <div class="tw-p-4 sm:tw-p-5">
      <div class="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
        <div class="tw-min-w-0 tw-text-left">
          <div class="tw-text-base tw-font-semibold tw-text-slate-900">Users</div>
          <div class="tw-mt-0.5 tw-text-sm tw-text-slate-600">
            <span v-if="showAllUsers">All users on the platform.</span>
            <span v-else>Manage access for your company.</span>
          </div>
          <div v-if="total" class="tw-mt-1 tw-text-xs tw-text-slate-500">
            {{ total }} user{{ total === 1 ? '' : 's' }}
            <span v-if="pageCount > 1"> · page {{ pageNum + 1 }} of {{ pageCount }}</span>
          </div>
        </div>

        <div class="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">
          <label class="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-700 tw-whitespace-nowrap">
            <span>Rows</span>
            <select
              v-model.number="pageSize"
              class="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/70 tw-px-2 tw-py-1.5 tw-text-sm tw-text-slate-900 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-900/10"
            >
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
          </label>

          <button
            v-if="canToggleAllUsers"
            type="button"
            class="tw-inline-flex tw-items-center tw-rounded-xl tw-border tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-shadow-sm tw-shadow-slate-900/5"
            :class="showAllUsers
              ? 'tw-border-slate-900 tw-bg-slate-900 tw-text-white hover:tw-bg-slate-800'
              : 'tw-border-slate-200/70 tw-bg-white/70 tw-text-slate-700 hover:tw-bg-white'"
            @click="toggleAllUsersView"
          >
            {{ showAllUsers ? 'My company only' : 'All users' }}
          </button>

          <button
            v-if="!showAllUsers"
            type="button"
            class="tw-inline-flex tw-items-center tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 tw-shadow-sm tw-shadow-slate-900/5 hover:tw-bg-white"
            @click="openAddUserModal"
          >
            Add New User
          </button>
        </div>
      </div>

      <div class="tw-mt-3 tw-max-w-xl">
        <SearchInput
          v-model="searchInput"
          @search="searchTermChanged"
          placeholder="Search by name or email…"
        />
        <div
          v-if="searchTerm"
          class="tw-mt-2 tw-text-[11px] tw-text-slate-500"
        >
          Searching for:
          <span class="tw-font-bold tw-text-slate-800">{{ searchTerm }}</span>
        </div>
      </div>
    </div>

    <div class="tw-border-t tw-border-slate-200/70">
      <div class="tw-overflow-auto">
        <table class="tw-w-full tw-border-collapse tw-text-left tw-text-sm tw-text-slate-800" key="users-table">
          <thead>
            <tr class="tw-border-b tw-border-slate-200/70 tw-bg-white/50">
              <th class="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-700">#</th>
              <th v-if="showAllUsers" class="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-700">Company</th>
              <th class="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-700">Name</th>
              <th class="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-700">Email</th>
              <th class="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-700">Role</th>
              <th class="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-700">Status</th>
              <th class="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="companyUsersLoading && !users.length">
              <td :colspan="tableColumnCount" class="tw-px-4 tw-py-8 tw-text-center tw-text-slate-500">
                Loading users…
              </td>
            </tr>
            <tr v-else-if="!users.length">
              <td :colspan="tableColumnCount" class="tw-px-4 tw-py-8 tw-text-center tw-text-slate-500">
                No users found.
              </td>
            </tr>
            <tr
              v-for="(companyUser, userIdx) in users"
              :key="companyUser.id"
              class="tw-border-b tw-border-slate-200/50 hover:tw-bg-slate-50/70"
            >
              <td class="tw-px-4 tw-py-3 tw-text-slate-600">{{ rowNumber(userIdx) }}</td>
              <td v-if="showAllUsers" class="tw-px-4 tw-py-3 tw-text-slate-700">
                {{ companyUser.company?.name || '—' }}
              </td>
              <td class="tw-px-4 tw-py-3 tw-font-medium tw-text-slate-900">{{ companyUser.fullName }}</td>
              <td class="tw-px-4 tw-py-3 tw-text-slate-700">{{ companyUser.email }}</td>

              <td class="tw-px-4 tw-py-3">
                <span
                  class="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-slate-200/70 tw-bg-white/60 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-slate-700"
                >
                  {{ formatUserRole(companyUser) }}
                </span>
              </td>

              <td class="tw-px-4 tw-py-3">
                <span
                  v-if="companyUser.isActive !== false"
                  class="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-emerald-200/70 tw-bg-emerald-50/70 tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-emerald-900"
                >
                  Active
                </span>
                <span
                  v-else
                  class="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-amber-200/70 tw-bg-amber-50/70 tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-amber-900"
                >
                  Inactive
                </span>
              </td>

              <td class="tw-px-4 tw-py-3">
                <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                  <button
                    v-if="canEditUser(companyUser)"
                    type="button"
                    class="tw-inline-flex tw-items-center tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-slate-700 hover:tw-bg-white"
                    @click="openEditUserModal(companyUser)"
                    title="Edit user"
                  >
                    Edit
                  </button>

                  <button
                    v-if="canEditUser(companyUser) && companyUser.isActive !== false"
                    type="button"
                    class="tw-inline-flex tw-items-center tw-rounded-xl tw-border tw-border-rose-200/70 tw-bg-rose-50/70 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-rose-900 hover:tw-bg-rose-50"
                    @click="openConfirmUserStatus(companyUser, false)"
                    title="Deactivate this user"
                  >
                    Deactivate
                  </button>

                  <button
                    v-if="canEditUser(companyUser) && companyUser.isActive === false"
                    type="button"
                    class="tw-inline-flex tw-items-center tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-slate-700 hover:tw-bg-white"
                    @click="openConfirmUserStatus(companyUser, true)"
                    title="Activate this user"
                  >
                    Activate
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="tw-mt-6 tw-flex tw-justify-center tw-px-4 tw-pb-4" v-if="pageCount > 1">
      <PaginationBar
        :page="pageNum"
        :totalPages="pageCount"
        :disabled="!!companyUsersLoading"
        @previousPage="prevPage"
        @nextPage="nextPage"
        @goToPage="goToPage"
      />
    </div>

    <Modal v-model:isOpen="isAddUserModalOpen" modalId="admin-users-add-modal" maxWidth="2xl" @close="closeAddUserModal">
      <template #title>Add new user</template>
      <template #content>
        <div class="tw-space-y-4">
        <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
          <InputField v-model="additionUser.name" label="Name" autocomplete="username" />
          <InputField v-model="additionUser.email" label="Email" autocomplete="email" />
          <InputField v-model="additionUser.password" label="Password" type="password" autocomplete="new-password" />
          <div class="tw-flex tw-items-end">
            <label class="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-700 tw-w-full tw-h-[40px]">
              <input
                type="checkbox"
                id="addition_user.isAdmin"
                v-model="additionUser.isAdmin"
                class="tw-h-4 tw-w-4 tw-rounded tw-border-slate-300"
              />
              Is admin
            </label>
          </div>
        </div>

        <div class="tw-mt-5 tw-flex tw-justify-end tw-gap-2">
          <Button :isGreen="true" icon="check" @click="saveUser">Save</Button>
          <Button :isRed="true" :isSecondary="true" @click="closeAddUserModal">Cancel</Button>
        </div>
        </div>
      </template>
    </Modal>

    <Modal
      v-model:isOpen="isEditUserModalOpen"
      modalId="admin-users-edit-modal"
      maxWidth="2xl"
      @close="closeEditUserModal"
    >
      <template #title>{{ editUserModalTitle }}</template>
      <template #content>
      <div class="tw-space-y-4">
        <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
          <InputField v-model="editingUser.name" label="Name" autocomplete="name" />
          <div>
            <div class="tw-block tw-mb-1 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-500">
              Email
            </div>
            <div class="tw-rounded-xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-3 tw-py-2 tw-text-sm tw-text-slate-600">
              {{ editingUser.email || '—' }}
            </div>
          </div>
          <InputField
            v-model="editingUser.password"
            label="New password"
            type="password"
            autocomplete="new-password"
            placeholder="Leave blank to keep current password"
          />
          <div class="tw-flex tw-flex-col tw-gap-3 tw-justify-end">
            <label class="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-700">
              <input
                type="checkbox"
                v-model="editingUser.isAdmin"
                class="tw-h-4 tw-w-4 tw-rounded tw-border-slate-300"
              />
              Company admin
            </label>
          </div>
        </div>

        <div class="tw-mt-5 tw-flex tw-justify-end tw-gap-2">
          <Button
            :isGreen="true"
            icon="check"
            :enableLoading="isSavingEditUser"
            :disabled="isSavingEditUser"
            @click="saveEditedUser"
          >
            Save
          </Button>
          <Button :isRed="true" :isSecondary="true" :disabled="isSavingEditUser" @click="closeEditUserModal">
            Cancel
          </Button>
        </div>
      </div>
      </template>
    </Modal>

    <Modal v-model:isOpen="isConfirmUserStatusModalOpen" modalId="admin-users-confirm-status-modal" maxWidth="lg" @close="closeConfirmUserStatus">
      <template #title>{{ confirmUserStatusTitle }}</template>
      <template #content>
        <div class="tw-space-y-4">
        <div class="tw-text-sm tw-text-slate-700">
          Are you sure you want to {{ pendingUserStatusIsActive ? 'activate' : 'deactivate' }}
          <span class="tw-font-semibold tw-text-slate-900">{{ pendingUserDisplayName }}</span>?
        </div>

        <div class="tw-mt-5 tw-flex tw-justify-end tw-gap-2">
          <Button
            v-if="pendingUserStatusIsActive"
            :isGreen="true"
            icon="check"
            :disabled="!pendingUserId"
            @click="confirmUserStatus"
          >
            Activate
          </Button>
          <Button
            v-else
            :isRed="true"
            icon="check"
            :disabled="!pendingUserId"
            @click="confirmUserStatus"
          >
            Deactivate
          </Button>
          <Button :isRed="true" :isSecondary="true" @click="closeConfirmUserStatus">Cancel</Button>
        </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import AddCompanyUser from '@/gql/company/mutations/AddNewCompanyUser.gql';
import EditUserMutation from '@/gql/user/EditUser.gql';
import EditUserStatusMutation from '@/gql/user/EditUserStatus.gql';
import CompanyUsersQuery from '@/gql/user/CompanyUsers.gql';
import CurrentUserQuery from '@/gql/user/user.gql';
import { pushToast } from '@/lib/toast';
import Button from '../core/Button.vue';
import InputField from '../core/Input/InputField.vue';
import Modal from '@/components/core/Modal/Modal.vue';
import PaginationBar from '@/components/core/PageElements/PaginationBar.vue';
import SearchInput from '@/components/core/SearchInput.vue';

export default defineComponent({
  props: ['company'],
  components: {
    Button,
    InputField,
    Modal,
    PaginationBar,
    SearchInput,
  },
  apollo: {
    currentUser: {
      query: CurrentUserQuery,
      update: data => data.user,
    },
    companyUsers: {
      query: CompanyUsersQuery,
      loadingKey: 'companyUsersLoading',
      debounce: 200,
      variables() {
        const vm = this as any;
        return {
          pageNum: vm.pageNum,
          pageSize: vm.pageSize,
          allUsers: vm.showAllUsers,
          searchQuery: vm.searchTerm || null,
        };
      },
      update: (data: any) => (data ? data.companyUsers : null),
      result(result: any) {
        const vm = this as any;
        const payload = result?.data?.companyUsers;
        vm.users = payload?.users || [];
        vm.total = payload?.total || 0;
        vm.pageCount = payload?.pages || 1;
      },
      error(err: any) {
        console.error(err);
        pushToast('Failed to load users', 'error');
      },
    },
  },
  data() {
    return {
      currentUser: null,
      companyUsersLoading: 0,
      users: [] as any[],
      total: 0,
      showAllUsers: false,
      searchInput: '',
      searchTerm: '',
      pageNum: 0,
      pageSize: 25,
      pageCount: 1,
      isAddUserModalOpen: false,
      isEditUserModalOpen: false,
      isSavingEditUser: false,
      isConfirmUserStatusModalOpen: false,
      editingUser: {
        id: null as number | null,
        name: '',
        email: '',
        password: '',
        isAdmin: false,
      },
      pendingUserId: null as any,
      pendingUserDisplayName: '' as string,
      pendingUserStatusIsActive: true,
      additionUser: {
        name: undefined,
        password: undefined,
        isAdmin: false,
        email: undefined,
      },
    };
  },
  computed: {
    canToggleAllUsers(): boolean {
      return !!(this as any).currentUser?.isSuperuser;
    },
    tableColumnCount(): number {
      return (this as any).showAllUsers ? 7 : 6;
    },
    confirmUserStatusTitle(): string {
      const vm = this as any;
      return vm.pendingUserStatusIsActive ? 'Activate user' : 'Deactivate user';
    },
    editUserModalTitle(): string {
      const vm = this as any;
      const label = vm.editingUser?.name || vm.editingUser?.email || 'user';
      return `Edit ${label}`;
    },
  },
  watch: {
    pageSize() {
      const vm = this as any;
      if (vm.pageNum !== 0) {
        vm.pageNum = 0;
      }
    },
  },
  methods: {
    rowNumber(indexOnPage: number): number {
      const vm = this as any;
      return vm.pageNum * vm.pageSize + indexOnPage + 1;
    },
    formatUserRole(user: any): string {
      if (user?.isSuperuser) {
        return 'Superuser';
      }
      if (user?.isAdmin) {
        return 'Admin';
      }
      return 'User';
    },
    canEditUser(user: any): boolean {
      const vm = this as any;
      const actor = vm.currentUser;
      // if current user is not authenticated or user id is missing, return false
      if (!actor || !user?.id) {
        return false;
      }

      // if self, cannot edit (must edit self from Account screen)
      if (user.id === actor.id) {
        return false;
      }

      // if user to edit is superuser, can't edit; 
      // (disallow editing any aspect of superusers, except for self from Account screen)
      if (user.isSuperuser) {
        return false;
      }

      // if not admin/super user, can't edit any user
      if (!actor.isAdmin && !actor.isSuperuser) {
        return false;
      }

      // if either admin OR superuser OR both
      // if NOT superuser, i.e. IS admin user
      if (!actor.isSuperuser) {
        const actorCompanyId = actor.company?.id;
        const targetCompanyId = user.company?.id;
        // only allow editing admin user's own company users
        if (actorCompanyId == null || targetCompanyId == null || actorCompanyId !== targetCompanyId) {
          return false;
        }
      }
      // if superuser, can edit any user
      return true;
    },
    openEditUserModal(user: any) {
      const vm = this as any;
      if (!vm.canEditUser(user)) {
        return;
      }
      vm.editingUser = {
        id: user.id,
        name: user.fullName || '',
        email: user.email || '',
        password: '',
        isAdmin: !!user.isAdmin,
      };
      vm.isEditUserModalOpen = true;
    },
    closeEditUserModal() {
      const vm = this as any;
      if (vm.isSavingEditUser) {
        return;
      }
      vm.isEditUserModalOpen = false;
      vm.editingUser = {
        id: null,
        name: '',
        email: '',
        password: '',
        isAdmin: false,
      };
    },
    saveEditedUser() {
      const vm = this as any;
      if (vm.isSavingEditUser || !vm.editingUser?.id) {
        return;
      }
      if (!vm.editingUser.name?.trim()) {
        pushToast('Name is required', 'warning');
        return;
      }

      const variables: Record<string, unknown> = {
        userId: parseInt(String(vm.editingUser.id), 10),
        name: vm.editingUser.name.trim(),
      };

      variables.isAdmin = !!vm.editingUser.isAdmin;

      const password = vm.editingUser.password?.trim();
      if (password) {
        variables.password = password;
      }

      vm.isSavingEditUser = true;
      vm.$apollo
        .mutate({
          mutation: EditUserMutation,
          variables,
        })
        .then(() => {
          pushToast('User updated', 'success');
          vm.closeEditUserModal();
          vm.refetchUsers();
        })
        .catch((error: any) => {
          console.error(error);
          pushToast('Failed to update user', 'error');
        })
        .finally(() => {
          vm.isSavingEditUser = false;
        });
    },
    refetchUsers() {
      const vm = this as any;
      if (vm.$apollo?.queries?.companyUsers) {
        vm.$apollo.queries.companyUsers.refetch();
      }
    },
    toggleAllUsersView() {
      const vm = this as any;
      if (!vm.canToggleAllUsers) {
        return;
      }
      vm.showAllUsers = !vm.showAllUsers;
      vm.pageNum = 0;
      vm.refetchUsers();
    },
    prevPage() {
      const vm = this as any;
      if (vm.companyUsersLoading) return;
      if (vm.pageNum > 0) vm.pageNum -= 1;
    },
    nextPage() {
      const vm = this as any;
      if (vm.companyUsersLoading) return;
      if (vm.pageNum + 1 >= vm.pageCount) return;
      vm.pageNum += 1;
    },
    goToPage(idx: number) {
      const vm = this as any;
      if (vm.companyUsersLoading) return;
      if (idx === vm.pageNum) return;
      if (idx < 0 || idx >= vm.pageCount) return;
      vm.pageNum = idx;
    },
    searchTermChanged(newSearchTerm: string) {
      const vm = this as any;
      if (vm.searchTerm !== newSearchTerm) {
        vm.searchTerm = newSearchTerm;
        vm.pageNum = 0;
      } else if (vm.pageNum !== 0) {
        vm.pageNum = 0;
      }
    },
    openAddUserModal() {
      const vm = this as any;
      vm.isAddUserModalOpen = true;
    },
    closeAddUserModal() {
      const vm = this as any;
      vm.isAddUserModalOpen = false;
      vm.additionUser = {
        name: undefined,
        isAdmin: false,
        email: undefined,
        password: undefined,
      };
    },

    openConfirmUserStatus(user: any, isActive: boolean) {
      const vm = this as any;
      
      // THESE two checks were initially BELOW canEditUser()
      // but they were covered by canEditUser() so they wouldn't be even reached
      // these defensive anyway; the template if/else rules above prevent this from happening
      if (!isActive && vm.currentUser && user.id === vm.currentUser.id) {
        pushToast('You cannot deactivate your own account', 'error');
        return;
      }
      if (user?.isSuperuser) {
        pushToast('Superuser cannot be edited', 'error');
        return;
      }

      if (!vm.canEditUser(user)) {
        return;
      }
      

      vm.pendingUserId = user?.id ?? null;
      vm.pendingUserDisplayName = user?.fullName || user?.email || 'this user';
      vm.pendingUserStatusIsActive = !!isActive;
      vm.isConfirmUserStatusModalOpen = true;
    },
    closeConfirmUserStatus() {
      const vm = this as any;
      vm.isConfirmUserStatusModalOpen = false;
      vm.pendingUserId = null;
      vm.pendingUserDisplayName = '';
      vm.pendingUserStatusIsActive = true;
    },
    confirmUserStatus() {
      const vm = this as any;
      if (!vm.pendingUserId) return;
      vm.updateUserStatus(vm.pendingUserId, vm.pendingUserStatusIsActive);
      vm.closeConfirmUserStatus();
    },

    updateUserStatus(userId: any, isActive: boolean) {
      const vm = (this as any);

      vm.$apollo.mutate({
        mutation: EditUserStatusMutation,
        variables: {
          userId: parseInt(userId, 10),
          isActive: isActive
        }
      }).then(() => {
        pushToast(`User ${isActive ? 'activated' : 'deactivated'} successfully`, 'success');
        vm.refetchUsers();
      }).catch((error) => {
        console.error(error);
        pushToast('Failed to update user status', 'error');
      });
    },

    saveUser() {
      const vm = (this as any);

      if (!vm.additionUser.name) {
        pushToast('Name is required', 'warning');
        return;
      }
      if (!vm.additionUser.email) {
        pushToast('Email is required', 'warning');
        return;
      }
      if (!vm.additionUser.password) {
        pushToast('Password is required', 'warning');
        return;
      }

      vm.$apollo
        .mutate({
          mutation: AddCompanyUser,
          variables: {
            name: vm.additionUser.name,
            isAdmin: vm.additionUser.isAdmin,
            password: vm.additionUser.password,
            email: vm.additionUser.email,
          },
        })
        .then((result) => {
          vm.closeAddUserModal();
          pushToast('User created', 'success');
          vm.refetchUsers();
        })
        .catch((error) => {
          console.error(error);
          pushToast('Failed to create user', 'error');
        });
    },
  },
} as any);
</script>